import { z } from 'zod';
import { formIntakeSchema, formSubmissionSchema } from '../types/form-settings.js';

export const MAX_REPEAT_COUNT = 15;

// Field type enum matching new types
export const FieldTypeSchema = z.enum([
  'text',
  'textarea',
  'date',
  'datetime',
  'select',
  'multiselect',
  'checkbox',
  'radio',
  'file',
  'static',
  'variable',
]);

export const VariableKeySchema = z.enum(['rep_subject_id', 'rep_subject_name']);

// Validation rule schema
export const ValidationRuleSchema = z.object({
  type: z.enum(['required', 'minLength', 'maxLength', 'min', 'max', 'pattern']),
  value: z.union([z.string(), z.number()]).optional(),
  message: z.string(),
});

// Select option schema
export const SelectOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.string(),
  defaultSelected: z.boolean().default(false),
});

// Section definition schema
// showInChat defaults to false if not provided, but should always be present in the output
export const SectionDefinitionSchema = z.object({
  id: z.string(),
  title: z.string(),
  order: z.number(),
  description: z.string().optional(),
  showInChat: z.boolean().prefault(false),
});

// Field definition schema
export const FieldDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string(),
  type: FieldTypeSchema,
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  required: z.boolean(),
  fullWidth: z.boolean().prefault(false),
  validationRules: z.array(ValidationRuleSchema),
  options: z.array(SelectOptionSchema).optional(),
  defaultValue: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]).optional(),
  variableKey: VariableKeySchema.optional().nullable(),
  visibleToUser: z.boolean().prefault(true),
  order: z.number(),
  sectionId: z.string().optional(),
});

export const FormPromptSchema = z.object({
  initialMessage: z.string(),
  systemPrompt: z.string().optional(),
});

// Form Schema Version (child table)
export const FormSchemaVersionSchema = z.object({
  id: z.number(),
  parentId: z.number(),
  version: z.string(),
  prompt: FormPromptSchema,
  fields: z.array(FieldDefinitionSchema),
  sections: z.array(SectionDefinitionSchema).optional(),
  repeatable: z.boolean(),
  createdAt: z.string(),
  createdBy: z.number(),
  isActive: z.boolean(),
});

// Form Schema Parent (parent table)
export const FormSchemaParentSchema = z.object({
  id: z.number(),
  identifier: z.string(),
  name: z.string(),
  description: z.string(),
  brandId: z.number(),
  active: z.boolean(),
  type: z.enum(['Custom Agent', 'Orchestration', 'Profile']),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.number(),
  isDeleted: z.boolean(),
  deletedAt: z.string().nullable(),
  deletedBy: z.number().nullable(),
  enhanceWithAi: z.boolean().optional().prefault(false),
  enhanceWithAiPromptIdentifier: z.string().optional().nullable(),
  downloadPdf: z.boolean().optional().prefault(false),
});

// Combined Form Schema (for API responses)
export const FormSchemaModel = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  brandId: z.number(),
  active: z.boolean(),
  type: z.enum(['Custom Agent', 'Orchestration', 'Profile']),
  version: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  prompt: FormPromptSchema,
  fields: z.array(FieldDefinitionSchema),
  sections: z.array(SectionDefinitionSchema).optional(),
  repeatable: z.boolean(),
  parentId: z.number(),
  versionId: z.number(),
  isActive: z.boolean(),
  isDeleted: z.boolean(),
  crmConnectionId: z.string().optional().nullable(),
  crmProvider: z.string().optional(),
  crmObjectName: z.string().optional().nullable(),
  // Optional AI type (parent-level)
  aiType: z.enum(['full', 'extraction', 'demo', 'production']).optional().nullable(),
  // How the form is started and what happens when it is submitted. Null clears them, and the defaults apply.
  intake: formIntakeSchema.nullable().optional(),
  submission: formSubmissionSchema.nullable().optional(),
  // Deprecated: kept for backward compatibility
  featurePropName: z.string().optional(),
  // Rich content endpoint for displaying HTML/Markdown content
  richContentEndpoint: z.string().optional().nullable(),
  // Full PDF filename template (without extension). Supports {date-fns-pattern} placeholders.
  pdfFilenameTemplate: z.string().optional().nullable(),
  // Probing feature toggle
  probing: z.boolean().optional().nullable(),
  // Enhance with AI
  enhanceWithAi: z.boolean().optional().prefault(false),
  enhanceWithAiPromptIdentifier: z.string().optional().nullable(),
  // Controls whether PDF should auto-download on submit/save
  downloadPdf: z.boolean().optional().prefault(false),
  // Identity of the intent this form serves; brand variants share one. Optional so a client parsing a
  // response from a server that predates lineage still works.
  lineageId: z.string().optional(),
  // The workflows this form is a step of, each once. Absent from a server that predates steps.
  usedInWorkflows: z
    .array(z.object({ identifier: z.string(), name: z.string() }))
    .optional(),
});

// Request schemas
export const CreateFormSchemaRequestSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().prefault(''),
  brandId: z.number(),
  active: z.boolean().prefault(true),
  type: z.enum(['Custom Agent', 'Orchestration', 'Profile']).prefault('Custom Agent'),
  version: z.string().prefault('1.0.0'),
  prompt: FormPromptSchema,
  fields: z.array(FieldDefinitionSchema),
  sections: z.array(SectionDefinitionSchema).optional(),
  repeatable: z.boolean().optional(),
  crmConnectionId: z.string().optional().nullable(),
  crmProvider: z.string().optional(),
  crmObjectName: z.string().optional().nullable(),
  aiType: z.enum(['full', 'extraction', 'demo', 'production']).optional().nullable(),
  // How the form is started and what happens when it is submitted. Null clears them, and the defaults apply.
  intake: formIntakeSchema.nullable().optional(),
  submission: formSubmissionSchema.nullable().optional(),
  // Deprecated: kept for backward compatibility
  featurePropName: z.string().optional(),
  // Rich content endpoint for displaying HTML/Markdown content
  richContentEndpoint: z.string().optional().nullable(),
  // Full PDF filename template (without extension). Supports {date-fns-pattern} placeholders.
  pdfFilenameTemplate: z.string().optional().nullable(),
  // Probing feature toggle
  probing: z.boolean().optional().nullable(),
  // Enhance with AI
  enhanceWithAi: z.boolean().optional(),
  enhanceWithAiPromptIdentifier: z.string().optional().nullable(),
  // Controls whether PDF should auto-download on submit/save
  downloadPdf: z.boolean().optional(),
  // Makes this form a copy of, or a brand clone of, an existing form. The server sets lineage and
  // provenance from the source; clients never send them directly.
  derivedFrom: z
    .object({
      identifier: z.string().min(1),
      kind: z.enum(['copy', 'brand_clone']),
    })
    .optional(),
});

export const UpdateFormSchemaRequestSchema = z.object({
  name: z.string().min(1, 'Name is required').optional(),
  description: z.string().optional(),
  brandId: z.number().optional(),
  active: z.boolean().optional(),
  type: z.enum(['Custom Agent', 'Orchestration', 'Profile']).optional(),
  version: z.string().optional(),
  prompt: FormPromptSchema.optional(),
  fields: z.array(FieldDefinitionSchema).optional(),
  sections: z.array(SectionDefinitionSchema).optional(),
  repeatable: z.boolean().optional(),
  createNewVersion: z.boolean().optional(),
  crmConnectionId: z.string().optional().nullable(),
  crmProvider: z.string().optional(),
  crmObjectName: z.string().optional().nullable(),
  aiType: z.enum(['full', 'extraction', 'demo', 'production']).optional().nullable(),
  // How the form is started and what happens when it is submitted. Null clears them, and the defaults apply.
  intake: formIntakeSchema.nullable().optional(),
  submission: formSubmissionSchema.nullable().optional(),
  // Deprecated: kept for backward compatibility
  featurePropName: z.string().optional(),
  // Rich content endpoint for displaying HTML/Markdown content
  richContentEndpoint: z.string().optional().nullable(),
  // Full PDF filename template (without extension). Supports {date-fns-pattern} placeholders.
  pdfFilenameTemplate: z.string().optional().nullable(),
  // Probing feature toggle
  probing: z.boolean().optional().nullable(),
  // Enhance with AI
  enhanceWithAi: z.boolean().optional(),
  enhanceWithAiPromptIdentifier: z.string().optional().nullable(),
  // Controls whether PDF should auto-download on submit/save
  downloadPdf: z.boolean().optional(),
});

export const GetFormSchemaParamsSchema = z.object({
  identifier: z.string(),
});

// Query string for listing forms, filtered by brand (id or identifier), active status and feature. The app API and
// the external API accept the same query.
export const GetFormSchemasQuerySchema = z.object({
  brandId: z
    .string()
    .optional()
    .transform((val) => (val ? Number(val) : undefined)),
  brandIdentifier: z.string().optional(),
  active: z
    .string()
    .optional()
    .transform((val) => (val ? val === 'true' : undefined)),
  featurePropName: z.string().optional(),
});

export const GetFormSchemaByIdParamsSchema = z.object({
  id: z.string().transform((val) => parseInt(val, 10)),
});

// Response schemas
export const GetFormSchemasResponseSchema = z.object({
  formSchemas: z.array(FormSchemaModel),
});

export const GetFormSchemaResponseSchema = z.object({
  formSchema: FormSchemaModel,
});

export const CreateFormSchemaResponseSchema = z.object({
  formSchema: FormSchemaModel,
});

export const UpdateFormSchemaResponseSchema = z.object({
  formSchema: FormSchemaModel,
});

export const DeleteFormSchemaResponseSchema = z.object({
  success: z.boolean(),
});

// Types
export type FieldType = z.infer<typeof FieldTypeSchema>;
export type VariableKey = z.infer<typeof VariableKeySchema>;
export type ValidationRule = z.infer<typeof ValidationRuleSchema>;
export type SelectOption = z.infer<typeof SelectOptionSchema>;
export type SectionDefinition = z.infer<typeof SectionDefinitionSchema>;
export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>;
export type FormPrompt = z.infer<typeof FormPromptSchema>;
export type FormSchema = z.infer<typeof FormSchemaModel>;
export type FormSchemaParent = z.infer<typeof FormSchemaParentSchema>;
export type FormSchemaVersion = z.infer<typeof FormSchemaVersionSchema>;
export type CreateFormSchemaRequest = z.infer<typeof CreateFormSchemaRequestSchema>;
export type UpdateFormSchemaRequest = z.infer<typeof UpdateFormSchemaRequestSchema>;
export type GetFormSchemaParams = z.infer<typeof GetFormSchemaParamsSchema>;
export type GetFormSchemasQuery = z.infer<typeof GetFormSchemasQuerySchema>;
export type GetFormSchemaByIdParams = z.infer<typeof GetFormSchemaByIdParamsSchema>;
export type GetFormSchemasResponse = z.infer<typeof GetFormSchemasResponseSchema>;
export type GetFormSchemaResponse = z.infer<typeof GetFormSchemaResponseSchema>;

// @deprecated Use AIFormState from @trinity/chat/types/forms instead
// This is kept only for backward compatibility with legacy API responses
export const aiFormStateSchema = z.object({
  originalExtraction: z
    .array(
      z.object({
        fieldLabel: z.string(),
        value: z.unknown(),
      })
    )
    .optional(),
  currentData: z
    .array(
      z.object({
        fieldLabel: z.string(),
        value: z.unknown(),
      })
    )
    .optional(),
  processingMetadata: z
    .object({
      extractionModel: z.string().optional(),
      extractionTime: z.number().optional(),
    })
    .optional(),
});

export const formDataItemSchema = z.object({
  fieldLabel: z.string(),
  value: z.unknown(),
});

/** @deprecated Use AIFormState from @trinity/chat/types/forms instead */
export type AIFormState = z.infer<typeof aiFormStateSchema>;
export type FormDataItem = z.infer<typeof formDataItemSchema>;

export type CreateFormSchemaResponse = z.infer<typeof CreateFormSchemaResponseSchema>;
export type UpdateFormSchemaResponse = z.infer<typeof UpdateFormSchemaResponseSchema>;
export type DeleteFormSchemaResponse = z.infer<typeof DeleteFormSchemaResponseSchema>;
